import React, { useCallback, useMemo, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { Card, Header, TextRow } from '../Profile/Profile.styles'
import {
  ConnectedEmailsWrapper,
  ConnectedEmailPill,
  Label,
  SettingsColumn,
  SettingsGrid,
  SettingsPreferencesRoot,
  SettingsRow,
  SettingsWideColumn
} from './SettingsAndPreferences.styles'
import { Tonality } from '@dsny/dsny-component-library'
import { Arrowback, Add, Info, Close } from '@dsny/dsny-component-library/icons'
import { thunks as ProfileThunks } from 'reducers/Profile'
import ModalActions from 'reducers/Modal'
import SelectInput from 'components/SelectInput'
import { ReleaseFrequency } from 'constants/enums'
import { useSearchParams } from 'react-router-dom'
import NotificationActions from 'reducers/Notification'
import UnlinkEmailModal from 'components/Modals/UnlinkEmailModal'
import { NotificationType } from 'constants/enums'
import { decodeToken } from 'utils/stringHelpers'

export const SettingsAndPreferences = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMobileMediaQuery()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2
  const profile = useSelector((state) => state.profile.profile)
  const [searchParams] = useSearchParams()
  const mergeToken = searchParams.get('merge')
  const splitToken = searchParams.get('split')
  const hasMultipleEmails = profile?.emails?.length > 1
  const [emailToUnlink, setEmailToUnlink] = useState(null)
  const [showUnlinkEmailModal, setShowUnlinkEmailModal] = useState(false)
  const token = localStorage.getItem('accessToken')
  const decodedAccessToken = token ? decodeToken(token) : null

  const handleBack = () => {
    window.history.back()
  }

  const releaseFrequencyOptions = useMemo(
    () =>
      Object.values(ReleaseFrequency).map((option) => ({
        value: option.value,
        label: t(option.display)
      })),
    []
  )

  const connectedEmailOptions = useMemo(
    () =>
      profile?.emails?.map((email) => ({
        value: email,
        label: email
      })) || [],
    [profile?.emails]
  )

  const saveProfile = (data) => {
    dispatch(
      ProfileThunks.postProfile({ ...profile, ...data }, () => {
        dispatch(
          NotificationActions.broadcastMessage(
            '',
            NotificationType.settingSaved
          )
        )
      })
    )
  }

  const updateSelectedFrequency = (newValue) => {
    saveProfile({ mailpref: newValue })
  }

  const updateMailTo = (email) => {
    saveProfile({ mailto: email })
  }

  const openConnectEmailModal = useCallback(() => {
    dispatch(ModalActions.toggleConnectEmailModal())
  }, [])

  const openUnlinkEmailModal = useCallback((email) => {
    setShowUnlinkEmailModal(true)
    setEmailToUnlink(email)
  }, [])

  useEffect(() => {
    if (mergeToken) {
      const decodedToken = decodeToken(mergeToken)

      dispatch(
        ProfileThunks.postMergeEmailThunks(
          mergeToken,
          () => {
            dispatch(ProfileThunks.getProfile())
            const url = new URL(window.location.href)
            url.searchParams.delete('merge')
            window.history.replaceState({}, '', url.toString())

            dispatch(
              NotificationActions.broadcastMessage(
                decodedToken?.invitation || '',
                NotificationType.emailSuccessfullyLinked,
                'success'
              )
            )
          },
          (error) => {
            dispatch(
              NotificationActions.broadcastMessage(
                decodedToken?.invitation || '',
                NotificationType.emailLinkingFailed,
                'error'
              )
            )
          }
        )
      )
    }

    if (splitToken) {
      const decodedToken = decodeToken(splitToken)

      dispatch(
        ProfileThunks.postSplitEmail(
          splitToken,
          () => {
            dispatch(ProfileThunks.getProfile())
            const url = new URL(window.location.href)
            url.searchParams.delete('split')
            window.history.replaceState({}, '', url.toString())

            dispatch(
              NotificationActions.broadcastMessage(
                decodedToken?.email || '',
                NotificationType.emailSuccessfullyUnlinked,
                'success'
              )
            )
          },
          (error) => {
            dispatch(
              NotificationActions.broadcastMessage(
                decodedToken?.email || '',
                NotificationType.emailUnlinkingFailed,
                'error'
              )
            )
          }
        )
      )
    }
  }, [mergeToken, splitToken])

  return (
    <>
      <SettingsPreferencesRoot
        $responsiveBreakpointsV2={responsiveBreakpointsV2}
        data-responsive-breakpoints={responsiveBreakpointsVersion}
      >
        <Header>
          <TextRow>
            <Tonality.UIHeading
              headingStyle="headline5"
              color={theme.colors.neutralW100}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              {isMobile && (
                <Arrowback
                  width={20}
                  height={20}
                  color={theme.colors.neutralW100}
                  style={{ marginRight: 10 }}
                  onClick={handleBack}
                />
              )}

              {t('settings.settings-preferences')}
            </Tonality.UIHeading>

            <Tonality.UIText
              textStyle="body"
              color={theme.colors.neutralW100}
              style={{ fontSize: 14 }}
            >
              {t('settings.settings-preferences-subtitle')}
            </Tonality.UIText>
          </TextRow>
        </Header>

        <Card>
          <SettingsGrid>
            <SettingsRow>
              <SettingsColumn>
                <Label
                  textStyle="bodyBold"
                  color={theme.colors.neutralW100}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  {t('settings.connected-emails')}

                  <Tonality.Tooltip
                    mode="dark"
                    hhasArrow={false}
                    placement="top"
                    content={t('settings.link-email-tooltip')}
                    trigger="hover"
                    containerStyles={{ width: 360 }}
                    children={
                      <span>
                        <Info width={16} height={16} />
                      </span>
                    }
                  />
                </Label>
              </SettingsColumn>

              <SettingsWideColumn>
                <ConnectedEmailsWrapper>
                  {profile?.emails?.length > 0 &&
                    profile?.emails.map((email, index) => {
                      const isPrimaryEmail =
                        decodedAccessToken?.username === email
                      const showCloseIcon = hasMultipleEmails && !isPrimaryEmail

                      return (
                        <ConnectedEmailPill
                          key={index}
                          variant="outlined"
                          mode="dark"
                          label={email}
                          icon={
                            showCloseIcon ? (
                              <Close width={16} height={16} />
                            ) : null
                          }
                          iconPosition="tailing"
                          onIconClick={() => openUnlinkEmailModal(email)}
                        />
                      )
                    })}

                  <Label
                    textStyle="body"
                    color={theme.colors.player50}
                    style={{
                      fontSize: 14,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                    onClick={openConnectEmailModal}
                  >
                    <Add width={16} />
                    {t('settings.add-another-email')}
                  </Label>
                </ConnectedEmailsWrapper>
              </SettingsWideColumn>
            </SettingsRow>

            <SettingsRow>
              <SettingsColumn>
                <Label
                  textStyle="bodyBold"
                  color={theme.colors.neutralW100}
                  style={{ fontSize: 14 }}
                >
                  {t('settings.where-receive-emails')}
                </Label>
              </SettingsColumn>

              <SettingsColumn>
                <SelectInput
                  popoverStyle={{ height: 'auto', maxHeight: 180 }}
                  selectOptions={connectedEmailOptions}
                  value={profile?.mailto}
                  onChangeValue={(val) => {
                    updateMailTo(val)
                  }}
                />
              </SettingsColumn>
            </SettingsRow>

            <SettingsRow>
              <SettingsColumn>
                <Label
                  textStyle="bodyBold"
                  color={theme.colors.neutralW100}
                  style={{ fontSize: 14 }}
                >
                  {t('settings.release-email-notification-frequency')}
                </Label>
              </SettingsColumn>

              <SettingsColumn>
                <SelectInput
                  popoverStyle={{ height: 180, maxHeight: 180 }}
                  selectOptions={releaseFrequencyOptions}
                  value={profile?.mailpref}
                  onChangeValue={(val) => {
                    updateSelectedFrequency(val)
                  }}
                />
              </SettingsColumn>
            </SettingsRow>
          </SettingsGrid>
        </Card>
      </SettingsPreferencesRoot>
      {showUnlinkEmailModal && (
        <UnlinkEmailModal
          isOpen={showUnlinkEmailModal}
          setIsOpen={setShowUnlinkEmailModal}
          email={emailToUnlink}
        />
      )}
    </>
  )
}

export default SettingsAndPreferences
